import { useMemo, useState } from 'react'

import { Temporal } from '@js-temporal/polyfill'

import { useIsSplitEnabled } from '@buffer-mono/features'

import { CURRENT_TIME_ZONE } from '~publish/helpers/dateFormatters'

interface CurrentTimeOverride {
  /**
   * The current time to use (either actual now or the preview override).
   */
  currentTime: Temporal.ZonedDateTime
  /**
   * Whether a preview date is currently set (vs using actual current time).
   */
  isPreviewSet: boolean
  /**
   * Set a preview date, or undefined to clear and use actual current time.
   */
  setPreviewDate: (date?: Temporal.ZonedDateTime) => void
  /**
   * Adjust the preview date by a number of days (positive or negative).
   * Only works when a preview date is set.
   */
  adjustPreviewByDays: (days: number) => void
}

export const useCurrentTimeOverride = (): CurrentTimeOverride => {
  const actualNowZoned = useMemo(
    () => Temporal.Now.zonedDateTimeISO(CURRENT_TIME_ZONE),
    [],
  )
  const [previewDate, setPreviewDate] = useState<
    Temporal.ZonedDateTime | undefined
  >(undefined)

  const isThemedTemplatesDateSelectorEnabled = useIsSplitEnabled(
    'themed-templates-internal-calendar',
  )

  const currentTime = useMemo(
    () =>
      isThemedTemplatesDateSelectorEnabled && previewDate
        ? previewDate
        : actualNowZoned,
    [isThemedTemplatesDateSelectorEnabled, previewDate, actualNowZoned],
  )

  const adjustPreviewByDays = useMemo(
    () =>
      (days: number): void => {
        setPreviewDate((current) => {
          const base = current ?? actualNowZoned
          return base.add({ days })
        })
      },
    [actualNowZoned],
  )

  return {
    currentTime,
    isPreviewSet: previewDate !== undefined,
    setPreviewDate,
    adjustPreviewByDays,
  }
}
